import React, { useEffect, useRef } from "react";

/**
 * Canvas-based rain overlay that adds gentle animated rainfall on top of the map.
 * Intensity represents roughly how many drops we render (1-10).
 * When showFlooding is true, puddles and ripple animations are overlaid behind the rain.
 */
const RainOverlay = ({ intensity = 5, style = {}, zIndex = 2, showFlooding = false }) => {
  const canvasRef = useRef(null);
  const puddleStateRef = useRef({
    puddles: [],
    hasShapes: false,
    width: 0,
    height: 0,
    lastIntensity: intensity,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const puddleState = puddleStateRef.current;
    if (puddleState.lastIntensity !== intensity) {
      puddleState.hasShapes = false;
      puddleState.lastIntensity = intensity;
    }

    let animationId;
    let drops = [];
    let splashes = [];

    const baseFlattenRatio = 0.55; // squish to get orthographic perspective

    const buildIrregularPoints = (count) => {
      const pts = [];
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count;
        const radiusFactor = 0.7 + Math.random() * 0.6; // 0.7 - 1.3
        pts.push({ angle, radiusFactor });
      }
      return pts;
    };

    const buildHoleSpecs = (count) => {
      return Array.from({ length: count }).map(() => ({
        xFactor: (Math.random() - 0.5) * 0.9,
        yFactor: (Math.random() - 0.5) * 0.7,
        radiusFactor: Math.random() * 0.25 + 0.12,
        stretchX: Math.random() * 0.4 + 0.8,
        stretchY: Math.random() * 0.4 + 0.7,
        rotation: Math.random() * Math.PI * 2,
      }));
    };

    const createPuddle = (width, height, overrides = {}) => {
      const maxRadius = overrides.maxRadius ?? (Math.random() * 90 + 90);
      const holeCount =
        overrides.holeCount ?? (Math.random() < 0.75 ? Math.floor(Math.random() * 3) : 0);
      const pointCount = overrides.pointCount ?? (12 + Math.floor(Math.random() * 8));
      const framesToMax = 60 * 5; // ~5 seconds
      const baseGrowth = maxRadius / framesToMax;
      const randomFactor = overrides.growthMultiplier ?? (0.9 + Math.random() * 0.2);
      const growthRate = (overrides.growthRate ?? baseGrowth) * randomFactor;
      return {
        x: overrides.x ?? Math.random() * width,
        y: overrides.y ?? Math.random() * height,
        maxRadius,
        currentRadius: overrides.initialRadius ?? 0,
        opacity: overrides.initialOpacity ?? 0,
        growthRate,
        shapePoints: buildIrregularPoints(pointCount),
        holes: buildHoleSpecs(Math.max(0, holeCount)),
        ripples: [],
        aspectRatio:
          overrides.aspectRatio ?? baseFlattenRatio * (0.9 + Math.random() * 0.2),
        shimmerPhase: Math.random() * Math.PI * 2,
        isPrimary: Boolean(overrides.isPrimary),
      };
    };

    const drawIrregularPath = (ctx, puddle) => {
      const pts = puddle.shapePoints;
      if (!pts.length || puddle.currentRadius <= 0) return;
      const scaled = pts.map((pt) => {
        const radius = puddle.currentRadius * pt.radiusFactor;
        return {
          x: puddle.x + Math.cos(pt.angle) * radius,
          y: puddle.y + Math.sin(pt.angle) * radius * puddle.aspectRatio,
        };
      });

      ctx.beginPath();
      ctx.moveTo(scaled[0].x, scaled[0].y);
      for (let i = 1; i < scaled.length; i++) {
        const prev = scaled[i - 1];
        const curr = scaled[i];
        const mid = { x: (prev.x + curr.x) / 2, y: (prev.y + curr.y) / 2 };
        ctx.quadraticCurveTo(prev.x, prev.y, mid.x, mid.y);
      }
      const last = scaled[scaled.length - 1];
      const first = scaled[0];
      ctx.quadraticCurveTo(last.x, last.y, (last.x + first.x) / 2, (last.y + first.y) / 2);
      ctx.closePath();
    };

    const drawHoleShape = (ctx, puddle, hole) => {
      const radius = puddle.currentRadius * hole.radiusFactor;
      const rx = Math.max(4, radius * hole.stretchX);
      const ry = Math.max(2, radius * hole.stretchY * puddle.aspectRatio);
      const cx = puddle.x + hole.xFactor * puddle.currentRadius;
      const cy = puddle.y + hole.yFactor * puddle.currentRadius * puddle.aspectRatio;

      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, hole.rotation, 0, Math.PI * 2);
    };

    const initDrops = (width, height) => {
      const dropCount = Math.max(10, Math.floor(intensity * 50));
      drops = Array.from({ length: dropCount }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        length: Math.random() * 20 + 10,
        speed: Math.random() * 10 + 15,
        opacity: Math.random() * 0.3 + 0.1,
        drift: Math.random() * 0.6 + 0.2,
      }));
    };

    const ensurePuddles = (width, height) => {
      const state = puddleStateRef.current;
      const sizeChanged =
        Math.abs(state.width - width) > 2 || Math.abs(state.height - height) > 2;

      if (sizeChanged) {
        state.width = width;
        state.height = height;
        if (!showFlooding) {
          state.hasShapes = false;
          return;
        }
      }

      if (!showFlooding) return;
      if (state.hasShapes && !sizeChanged) return;

      const puddleCount = Math.floor(intensity * 1.5) + 3;
      const mainRadius = Math.min(width, height) * 0.55;
      const puddleList = [
        createPuddle(width, height, {
          x: width / 2,
          y: height / 2,
          maxRadius: mainRadius,
          pointCount: 18 + Math.floor(Math.random() * 6),
          holeCount: 4 + Math.floor(Math.random() * 3),
          aspectRatio: baseFlattenRatio * 0.85,
          isPrimary: true,
        }),
      ];
      for (let i = 0; i < puddleCount; i++) {
        puddleList.push(createPuddle(width, height));
      }

      state.puddles = puddleList;
      state.hasShapes = true;
    };

    const createSplash = (x, y) => {
      splashes.push({
        x,
        y,
        radius: 0,
        maxRadius: Math.random() * 15 + 8,
        opacity: 0.6,
        rings: Math.floor(Math.random() * 2) + 2,
      });
    };

    const addPuddleRipple = (puddle) => {
      if (puddle.currentRadius <= 10) return;
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * puddle.currentRadius * 0.7;
      const rippleX = puddle.x + Math.cos(angle) * radius;
      const rippleY = puddle.y + Math.sin(angle) * radius * puddle.aspectRatio;
      puddle.ripples.push({
        x: rippleX,
        y: rippleY,
        radius: 0,
        maxRadius: Math.random() * 15 + 5,
        opacity: 0.5,
      });
    };

    const resizeCanvas = () => {
      const { offsetWidth, offsetHeight } = canvas;
      canvas.width = offsetWidth;
      canvas.height = offsetHeight;
      initDrops(offsetWidth, offsetHeight);
      ensurePuddles(offsetWidth, offsetHeight);
      splashes = [];
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (showFlooding && puddleStateRef.current.hasShapes) {
        const puddles = puddleStateRef.current.puddles;
        puddles.forEach((puddle) => {
          if (puddle.currentRadius < puddle.maxRadius) {
            puddle.currentRadius = Math.min(
              puddle.maxRadius,
              puddle.currentRadius + puddle.growthRate
            );
            puddle.opacity = Math.min(0.4, puddle.opacity + 0.002);
          }

          const gradient = ctx.createRadialGradient(
            puddle.x,
            puddle.y,
            0,
            puddle.x,
            puddle.y,
            puddle.currentRadius
          );
          gradient.addColorStop(0, `rgba(100, 140, 180, ${puddle.opacity * 0.8})`);
          gradient.addColorStop(0.5, `rgba(80, 120, 160, ${puddle.opacity * 0.6})`);
          gradient.addColorStop(1, `rgba(60, 100, 140, ${puddle.opacity * 0.25})`);

          drawIrregularPath(ctx, puddle);
          ctx.fillStyle = gradient;
          ctx.fill();

          ctx.save();
          ctx.globalCompositeOperation = "destination-out";
          puddle.holes.forEach((hole) => {
            drawHoleShape(ctx, puddle, hole);
            ctx.fill();
          });
          ctx.restore();

          drawIrregularPath(ctx, puddle);
          ctx.strokeStyle = `rgba(150, 180, 210, ${puddle.opacity * 0.5})`;
          ctx.lineWidth = 1;
          ctx.stroke();

          puddle.holes.forEach((hole) => {
            drawHoleShape(ctx, puddle, hole);
            ctx.strokeStyle = `rgba(150, 180, 210, ${puddle.opacity * 0.3})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          });

          if (Math.random() < 0.02 * intensity) {
            addPuddleRipple(puddle);
          }

          for (let i = puddle.ripples.length - 1; i >= 0; i--) {
            const ripple = puddle.ripples[i];
            ctx.beginPath();
            ctx.ellipse(
              ripple.x,
              ripple.y,
              ripple.radius,
              ripple.radius * 0.4,
              0,
              0,
              Math.PI * 2
            );
            ctx.strokeStyle = `rgba(180, 200, 220, ${ripple.opacity})`;
            ctx.lineWidth = 1;
            ctx.stroke();

            ripple.radius += 0.8;
            ripple.opacity -= 0.015;

            if (ripple.opacity <= 0 || ripple.radius > ripple.maxRadius) {
              puddle.ripples.splice(i, 1);
            }
          }

          const shimmerOffset = Date.now() * 0.001;
          const shimmerRadius = puddle.currentRadius * 0.4;
          const shimmerX =
            puddle.x + Math.sin(shimmerOffset + puddle.shimmerPhase) * shimmerRadius;
          const shimmerY =
            puddle.y +
            Math.cos(shimmerOffset * 0.8 + puddle.shimmerPhase) *
              shimmerRadius *
              puddle.aspectRatio;

          ctx.beginPath();
          ctx.ellipse(shimmerX, shimmerY, 8, 3, 0.3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(200, 220, 240, ${puddle.opacity * 0.35})`;
          ctx.fill();
        });
      }

      drops.forEach((drop) => {
        ctx.beginPath();
        ctx.moveTo(drop.x, drop.y);
        ctx.lineTo(drop.x + 1, drop.y + drop.length);
        ctx.strokeStyle = `rgba(174, 194, 224, ${drop.opacity})`;
        ctx.lineWidth = 1;
        ctx.lineCap = "round";
        ctx.stroke();

        drop.y += drop.speed;
        drop.x += drop.drift;

        if (Math.random() < 0.008) {
          createSplash(Math.random() * canvas.width, Math.random() * canvas.height);
        }

        if (drop.y > canvas.height) {
          createSplash(drop.x, canvas.height - 2);
          drop.y = -drop.length;
          drop.x = Math.random() * canvas.width;
        }
        if (drop.x > canvas.width) {
          drop.x = 0;
        }
      });

      for (let i = splashes.length - 1; i >= 0; i--) {
        const splash = splashes[i];

        for (let ring = 0; ring < splash.rings; ring++) {
          const ringOffset = ring * 4;
          const currentRadius = splash.radius - ringOffset;

          if (currentRadius > 0) {
            ctx.beginPath();
            ctx.ellipse(
              splash.x,
              splash.y,
              currentRadius,
              currentRadius * 0.4,
              0,
              0,
              Math.PI * 2
            );
            const ringOpacity = splash.opacity * (1 - ring * 0.3);
            ctx.strokeStyle = `rgba(174, 194, 224, ${Math.max(0, ringOpacity)})`;
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }
        }

        if (splash.radius < splash.maxRadius * 0.5) {
          const dropletCount = 3;
          for (let d = 0; d < dropletCount; d++) {
            const angle = (Math.PI / (dropletCount + 1)) * (d + 1);
            const dropletDist = splash.radius * 1.5;
            const dropletX = splash.x + Math.cos(angle) * dropletDist * (d % 2 === 0 ? 1 : -1);
            const dropletY = splash.y - Math.sin(angle) * dropletDist * 0.8;

            ctx.beginPath();
            ctx.arc(dropletX, dropletY, 1.5, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(174, 194, 224, ${splash.opacity * 0.8})`;
            ctx.fill();
          }
        }

        splash.radius += 1.5;
        splash.opacity -= 0.02;

        if (splash.opacity <= 0 || splash.radius > splash.maxRadius) {
          splashes.splice(i, 1);
        }
      }

      animationId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    animationId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationId);
    };
  }, [intensity, showFlooding]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex,
        ...style,
      }}
    />
  );
};

export default RainOverlay;
